// src/components/driver/DriverForm.js
import React from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  CircularProgress,
  Paper,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { getCompanyDriverGroups } from '../../api/driverGroupAPI';

// Define validation schema - now includes DriverGroupId when editing
const schema = yup.object({
  DriverFullName: yup.string().required('Driver name is required'),
  DriverEnabled: yup.boolean(),
  DriverEmail: yup.string().email('Invalid email format').nullable(),
  DriverPhone: yup.string().nullable(),
  DriverGroupId: yup.number().nullable().transform(value => 
    isNaN(value) || value === '' ? null : value
  ),
  DriverNotifActions: yup.boolean(),
  DriverNotifPayments: yup.boolean(),
  DriverNotifSystem: yup.boolean()
}).required();

const DriverForm = ({ driver, companyId, onSubmit, isLoading, showDriverGroupSelection = false }) => {
  // Fetch driver groups for the company when editing
  const { data: driverGroups, isLoading: isLoadingGroups } = useQuery({
    queryKey: ['company-driver-groups', companyId],
    queryFn: () => getCompanyDriverGroups(companyId),
    enabled: !!companyId && showDriverGroupSelection
  });

  const { control, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: driver ? {
      DriverFullName: driver.DriverFullName,
      DriverEnabled: driver.DriverEnabled,
      DriverEmail: driver.DriverEmail || '',
      DriverPhone: driver.DriverPhone || '',
      DriverGroupId: driver.DriverGroupId || '',
      DriverNotifActions: driver.DriverNotifActions,
      DriverNotifPayments: driver.DriverNotifPayments,
      DriverNotifSystem: driver.DriverNotifSystem,
    } : {
      DriverFullName: '',
      DriverEnabled: true,
      DriverEmail: '',
      DriverPhone: '',
      DriverGroupId: '',
      DriverNotifActions: false,
      DriverNotifPayments: false,
      DriverNotifSystem: false,
    }
  });

  const onFormSubmit = (data) => {
    // Add company ID to the form data
    const formData = {
      ...data,
      DriverCompanyId: companyId,
    };
    
    // Convert empty strings to null
    Object.keys(formData).forEach(key => {
      if (formData[key] === '') {
        formData[key] = null;
      }
    });
    
    onSubmit(formData);
  };

  return (
    <Paper elevation={2} sx={{ p: 3 }}>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Basic Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="DriverFullName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Full Name"
                  fullWidth
                  error={!!errors.DriverFullName}
                  helperText={errors.DriverFullName?.message}
                  required
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="DriverEnabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Driver Enabled"
                />
              )}
            />
          </Grid>

          {/* Contact Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="DriverEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Email"
                  type="email"
                  fullWidth
                  error={!!errors.DriverEmail}
                  helperText={errors.DriverEmail?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Controller
              name="DriverPhone"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Phone Number"
                  fullWidth
                  error={!!errors.DriverPhone}
                  helperText={errors.DriverPhone?.message}
                />
              )}
            />
          </Grid>

          {/* Driver Group Assignment - Only shown when editing */}
          {showDriverGroupSelection && (
            <>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>Driver Group Assignment</Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid item xs={12}>
                {isLoadingGroups ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : (
                  <Controller
                    name="DriverGroupId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.DriverGroupId}>
                        <InputLabel id="driver-group-select-label">Driver Group</InputLabel>
                        <Select
                          {...field}
                          labelId="driver-group-select-label"
                          label="Driver Group"
                        >
                          <MenuItem value="">
                            <em>No Group Assigned</em>
                          </MenuItem>
                          {driverGroups && driverGroups.map((group) => (
                            <MenuItem key={group.DriversGroupId} value={group.DriversGroupId}>
                              {group.DriversGroupName} 
                              {group.DriverTariffId && ` (Tariff ID: ${group.DriverTariffId})`}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.DriverGroupId && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.DriverGroupId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                )}
              </Grid>
            </>
          )}

          {/* Notification Settings */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>Notification Settings</Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="DriverNotifActions"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Action Notifications"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="DriverNotifPayments"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Payment Notifications"
                />
              )}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <Controller
              name="DriverNotifSystem"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="System Notifications"
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={isLoading}
                startIcon={isLoading ? <CircularProgress size={20} /> : null}
              >
                {driver ? 'Update' : 'Create'} Driver
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default DriverForm;